<template>
  <div
    id="annotationToolRibbon"
    class="ribbon"
  >
    <div class="diagonal-box">
      <p>AGTG</p>
    </div>
    <div class="ribbon__group">
      <!-- Commented out because confusing -->
      <!-- <div id="pending-annotation-counts">
        <div class="tag">
          <div class="tag__count"><span>{{ sam2Annotations.length }}</span></div>
          <span class="tag__label ms-2">Segmentations</span>
        </div>
        <div class="tag">
          <div class="tag__count"><span>{{ aigt2dAnnotations.length }}</span></div>
          <span class="tag__label ms-2">2D Boxes</span>
        </div>
        <div class="tag">
          <div class="tag__count"><span>{{ aigt3dAnnotations.length }}</span></div>
          <span class="tag__label ms-2">3D Boxes</span>
        </div>
      </div> -->
      <!-- <IconButton
        id="undoBtn"
        class="ribbon__button"
        aria-label="Undo"
        :disabled="!canUndo"
        :icon="'undo'"
        :showHover="false"
        :type="''"
        :title="'Undo'"
        @click="handleUndo"
      /> -->
    </div>
    <div class="ribbon__group">
      <hr class="ribbon__vr">
      <button
        class="button ribbon__button-secondary button-rounded"
        :class="{'button-spinner': isCreatingAnnotations}"
        :disabled="!hasInitState || isPropagating || isCreatingAnnotations || sam2Annotations.length === 0"
        :title="`Clear unsaved annotations`"
        @click="handleClearSAM2Annotations"
      >
        Clear Annotations
      </button>
      <button
        v-tooltip="`Save pending AIGT annotations and edits to this frame, such as joined objects`"
        class="button ribbon__button-primary button-rounded"
        :class="{'button-spinner': isCreatingAnnotations}"
        :disabled="!hasInitState || isPropagating || isCreatingAnnotations || (sam2Annotations.length <= 0 && !hasFrameEdits)"
        @click="handleSaveSAM2Annotations"
      >
        Save AIGT Annotations
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useToast } from 'vue-toast-notification';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useEditorStore } from '@/stores/useEditorStore.js';
import useViewerVisualizations from '@/composables/useViewerVisualizations.js';
import { useSAM2Store } from '@/stores/useSAM2Store.js';
import useTasks from '@/composables/annotationTool/useTasks.js';

const emit = defineEmits([
  'submit',
]);

const $toast = useToast();

const editorStore = useEditorStore();
const {
  labels,
  currentSample,
} = storeToRefs(editorStore);
const { setCurrentAnnotations, clearChangedAnnotationsForImage } = editorStore;

const {
  addNewAnnotationsArrayToCache,
  recacheAndSetCurrentSample,
} = useViewerVisualizations();

const { handleFrameLabellingSubmitAnnotations, hasUnsavedFrameChanges } = useTasks();

const hasFrameEdits = computed(() => hasUnsavedFrameChanges(currentSample.value?.imageObj));

const sam2Store = useSAM2Store();
const {
  sam2Options,
  sam2Annotations,
  aigt3dAnnotations,
  aigt2dAnnotations,
  hasInitState,
  isPropagating,
  isCreatingAnnotations,
} = storeToRefs(sam2Store);
const {
  handleSAMReset,
} = sam2Store;

// Polygons SAM generated for existing objects and other edits to the frame (e.g. joined objects) are saved through the regular frame submit.
async function saveFrameEdits(polygonUpdates) {
  const imageObj = currentSample.value.imageObj;
  if (polygonUpdates.length > 0) {
    const polygonById = new Map(polygonUpdates
      .filter((anno) => anno.image_id === imageObj.id)
      .map((anno) => [anno.existing_annotation_id, anno.polygon]));
    setCurrentAnnotations(currentSample.value.annotations.map((anno) => (polygonById.has(anno.id)
      ? {
        ...anno, polygon: polygonById.get(anno.id), save_pending: true, status: anno.status || 'updated',
      }
      : anno)));
  }
  await handleFrameLabellingSubmitAnnotations(imageObj);
  imageObj.annotations = currentSample.value.annotations;
  clearChangedAnnotationsForImage(imageObj.id);
  await recacheAndSetCurrentSample();
}

async function handleSaveSAM2Annotations() {
  isCreatingAnnotations.value = true;
  try {
    // Checked before SAM annotations are added so they are not counted as frame edits.
    const needsFrameSave = hasFrameEdits.value;
    const polygonUpdates = sam2Annotations.value.filter((anno) => anno.existing_annotation_id);
    const annotationsToAdd = [
      ...sam2Annotations.value.filter((anno) => !anno.existing_annotation_id),
      ...aigt3dAnnotations.value,
      ...aigt2dAnnotations.value,
    ];

    if (annotationsToAdd.length > 0) {
      const dataConnect = new DatastoreConnect();
      const resp = await dataConnect.addAnnotationsBulk({ annotations: annotationsToAdd });
      if (resp.error !== undefined) {
        throw Error(resp.error);
      }
      const newAnnotations = resp.result;
      // Set label_index and label_name for new annotations
      // TODO: remove the necessity to do this
      newAnnotations.forEach((anno) => {
        const label = labels.value.find((e) => e.id === anno.label_id);
        anno.label_index = label.index;
        anno.label_name = label.name;
      });

      // Update cache with new annotations
      addNewAnnotationsArrayToCache(newAnnotations);
    }

    if (polygonUpdates.length > 0 || needsFrameSave) {
      await saveFrameEdits(polygonUpdates);
    }

    // Reset SAM2
    await handleSAMReset();
  } catch (error) {
    console.error(error);
    const reason = error?.name === 'TimeoutError'
      ? 'the server did not respond in time. The save may still complete, so check the frame before retrying'
      : (error?.message || 'unknown error');
    $toast.open({
      queue: true, duration: 8000, message: `Failed to save annotations: ${reason}.`, type: 'error', position: 'top',
    });
    return;
  } finally {
    isCreatingAnnotations.value = false;
  }

  emit('submit');
}


async function handleClearSAM2Annotations() {
  sam2Options.value.params.clicks = [];
  sam2Options.value.params.box = [];
  sam2Options.value.params.boxes = [];
  sam2Annotations.value = [];
  aigt3dAnnotations.value = [];
  aigt2dAnnotations.value = [];
}

defineExpose({
  handleSaveSAM2Annotations,
});

</script>

<style lang="scss" scoped>
.diagonal-box {
  background-color: var(--color-primary-400);
  color: var(--primary-text-color);
  width: 100%;
  height: 100%;
  max-width: 100px;
  clip-path: polygon(0 0, 100% 0, 80% 100%, 0% 100%);
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
  padding-right: 34px;
  font-weight: 500;
}

.ribbon {
  position: relative;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  min-height: 37px;
  max-height: 37px;
  padding: 2px 16px;
  justify-content: space-between;
  z-index: 10;
  box-shadow: 0 3px 3px -3px gray, 0 10px 10px -10px gray;
  color: var(--ribbon-icons-color);
  background: var(--ribbon-bg);
  border-left: solid 1px var(--ribbon-border);

  &__group {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }

  &__vr {
    display: flex;
    width: 1px;
    height: 60%;
    margin: 0;
  }

  &__button {
    display: flex;
    min-width: 22px;
    height: 22px;
    color: inherit;
    background: none;
    border: none;
    font-size: 0.875rem;
    line-height: 1rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .05em;
    align-items: center;
    cursor: pointer;

    &:disabled {
      cursor: default;
      color: var(--color-disabled);
    }
  }

  &__button-primary {
    display: flex;
    height: 28px;
    font-size: 0.875rem;
    line-height: 1rem;
    align-items: center;

    &:hover:not(:disabled), &.active:not(:disabled) {
      background-color: var(--button-hover-color);
    }
  }

  &__button-secondary {
    background: white;
    @include themify() {
      color: themed('body-text-color')
    }
    display: flex;
    height: 28px;
    font-size: 0.875rem;
    line-height: 1rem;
    align-items: center;

    &:hover {
      background: rgba(var(--color-primary-100-rgb), 0.5);
    }

    &:disabled {
      cursor: default;
      color: var(--button-text-color);
    }
  }

  &__labeled_button {
    width: auto;
  }
}

:deep(.filter-button) {
  height: 22px;
}

#pending-annotation-counts {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 8px;
  margin-left: 92px;
}

.tag {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-radius: 18px;
  border: 2px solid var(--color-primary);
  padding: 2px 4px;

  &__count {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 18px;
    padding: 0 6px;
    height: 18px;
    width: fit-content;
    background: var(--color-accent);
  }

  &__label {
    font-weight: 600;
    letter-spacing: 0.025em;
    font-size: 0.875rem;
    line-height: 1;
  }
}

</style>
